// src/lib/utils/contactFilter/contactFilter.ts
//
// Shared helpers to filter out "no-reply" / technical contact addresses returned by contact
// search (Graph `/me/people` + `/search/query`).
//
// Context (AB#1744): contact search can surface technical addresses that are only a
// notification / transport channel (e.g. `no-reply@teams.mail.microsoft`, `noreply@sisurvey.eu`).
// This happens because the matched person's *displayName* contains the searched name while the
// resolved address (its `userPrincipalName` fallback) is a shared placeholder unrelated to the
// person. Callers choose how strict to be:
//   - Conversation / agenda assistant → drop them unconditionally (they always pollute).
//   - CustomView creation             → drop them ONLY when the searched term is not the
//                                        relevant identifier of the address, so a legit service
//                                        sender like `noreply@jotform.com` / `noreply@lucca.fr`
//                                        is kept (see `filterNoReplyContactsForQuery`).

import { normalizeString } from "../normalizeString/normalizeString";

/** Substrings that mark an address as a "no-reply" / technical sender. */
const NO_REPLY_MARKERS = ["noreply", "no-reply", "yammer.com"] as const;

/**
 * True when `email` is a technical "no-reply" / notification address
 * (contains `noreply`, `no-reply`, or `yammer.com`). Case-insensitive.
 */
export function isNoReplyAddress(email: string): boolean {
    const normalized = email.toLowerCase();
    return NO_REPLY_MARKERS.some((marker) => normalized.includes(marker));
}

/**
 * True when every word of `query` matches, at a word boundary, inside `email`.
 *
 * Mirrors `GraphManager`'s `matchesAllWords` matching: accent-insensitive normalization plus a
 * left word-boundary (`\s @ . _ -`), which enables prefix matching (e.g. "lucca" → "lucca.fr")
 * while avoiding false suffix matches. Used to decide whether a searched term is actually the
 * identifier carried by an address.
 */
export function contactMatchesQuery(email: string, query: string): boolean {
    const words = normalizeString(query)
        .split(/\s+/)
        .filter((word) => word.length > 0);
    if (words.length === 0) {
        return false;
    }

    const normalizedEmail = normalizeString(email);
    return words.every((word) => {
        const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const wordRegex = new RegExp(`(^|[\\s@._-])${escaped}`, "i");
        return wordRegex.test(normalizedEmail);
    });
}

/**
 * Filters out technical "no-reply" addresses that do NOT correspond to the searched term.
 *
 * Functional rule (AB#1744): a `no-reply` address is kept ONLY when it is the relevant identifier
 * of the searched sender (its address matches the query — e.g. "lucca" → `noreply@lucca.fr`), and
 * dropped when it is merely a notification / transport channel unrelated to the query (e.g.
 * "Xavier Malaurie" → `no-reply@teams.mail.microsoft`). Non no-reply addresses are always kept.
 *
 * @param contacts - Addresses resolved for a single `query` term.
 * @param query    - The searched term that produced `contacts`.
 */
export function filterNoReplyContactsForQuery(
    contacts: string[],
    query: string,
): string[] {
    return contacts.filter(
        (email) =>
            !isNoReplyAddress(email) || contactMatchesQuery(email, query),
    );
}
